import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import ee from "../../../services/earth-engine";
import { Box, Button, Typography } from "@material-ui/core";
import { FINALIZE, PREVIOUS } from "../../../hooks/useStepNavigations";
import GoogleMap from "../map/GoogleMap";
import StepperButtons from "../acquisition/StepperButtons";
import { OverlayHelper } from "./IntertidalZoneDefiner";
import { Actions as Snack } from "../../../store/ducks/snacks";

/**
 * @function VectorEditor
 * @description Step of the intertidal wizard that lets the user review the vectorized intertidal
 * zone polygons on the map and click individual polygons to mark them for removal before
 * finalizing the analysis. Deleted polygons are masked out of the cached AWEI/step-5 images so
 * the removal is reflected in the final results.
 * @param {Object} props - Component properties.
 * @param {Function} props.navigate - Step navigation handler.
 * @see {@link renderPolygons} - draws the editable polygons on the map.
 * @see {@link handlePolygonClick} - toggles a polygon's deletion state.
 * @see {@link handleApplyAndContinue} - applies the deletions and advances to the next step.
 * @see {@link OverlayHelper}
 * **Called by:
 * @see {IntertidalPage} - as one of the acquisition wizard's steps.
 * @memberof module:INTERTIDAL
 * @returns {JSX.Element} The map, polygon editor instructions, and stepper controls.
 */
const VectorEditor = ({ navigate, ...extra }) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const [editablePolygons, setEditablePolygons] = useState([]);
  const editablePolygonsRef = useRef([]);
  const [deletedFeatureIndices, setDeletedFeatureIndices] = useState(new Set());
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef(window.initMap);
  const boundsRef = useRef(null);
  const centerRef = useRef(null);
  const vectorsGeoJSONRef = useRef(null);
  const aoiPolygonRef = useRef(null);

  useEffect(() => {
    const checkInterval = setInterval(() => {
      if (window.initMap && window.initMap.getBounds) {
        mapRef.current = window.initMap;
        clearInterval(checkInterval);
        setMapReady(true);
      }
    }, 100);

    if (window.initMap && !mapReady) {
      const checkMapReady = () => {
        clearInterval(checkInterval);
        setMapReady(true);
      };

      try {
        window.google.maps.event.addListenerOnce(
          window.initMap,
          "tilesloaded",
          checkMapReady
        );
      } catch (e) {
        console.warn("Could not add listener to map:", e);
      }
    }

    return () => {
      clearInterval(checkInterval);
    };
  }, []);

  useEffect(() => {
    if (!mapReady) return;

    const coordinatesArray = JSON.parse(
      window.localStorage.getItem("AOIIntertidal")
    );

    if (!coordinatesArray) {
      console.error("No AOI coordinates found!");
      return;
    }

    const formatedCoordinates = coordinatesArray.map((coord) => {
      return { lat: coord[1], lng: coord[0] };
    });

    const AOI = new window.google.maps.Polygon({
      path: formatedCoordinates,
      geodesic: true,
      fillColor: "transparent",
      strokeColor: "#00A391",
      strokeOpacity: 1,
      strokeWeight: 2,
      clickable: false,
    });
    AOI.setMap(mapRef.current);
    aoiPolygonRef.current = AOI;

    const bounds = new window.google.maps.LatLngBounds();
    AOI.getPath()
      .getArray()
      .forEach((coord) => {
        let latLng = new window.google.maps.LatLng(coord.lat(), coord.lng());
        bounds.extend(latLng);
      });

    boundsRef.current = bounds;
    const center = bounds.getCenter();
    centerRef.current = center;
    mapRef.current.fitBounds(bounds);

    if (!OverlayHelper.vectorsToExport) {
      console.warn(
        "Nenhum vetor encontrado em OverlayHelper — voltando para a etapa anterior para recalcular a análise."
      );
      navigate(PREVIOUS);
      return;
    }

    try {
      const geoJson = OverlayHelper.vectorsToExport.getInfo();
      vectorsGeoJSONRef.current = geoJson;

      renderPolygons(geoJson, bounds);
    } catch (error) {
      console.error("Error loading vectors:", error);
    }
  }, [mapReady]);

  /**
   * @function renderPolygons
   * @description Draws each non-deleted feature from the vectorized GeoJSON as a clickable
   * editable Google Maps polygon on the map, wiring a click listener to toggle its deletion state.
   * @param {Object} geoJson - Vectorized intertidal zone GeoJSON (Polygon/MultiPolygon features).
   * @param {google.maps.LatLngBounds} bounds - AOI bounds; currently unused within the function body.
   * @see {@link handlePolygonClick}
   * **Called by:
   * @see {VectorEditor} - after the vectors finish loading.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const renderPolygons = (geoJson, bounds) => {
    const polygons = [];

    geoJson.features.forEach((feature, index) => {
      if (deletedFeatureIndices.has(index)) return;
      let paths;
      if (feature.geometry.type === "Polygon") {
        paths = feature.geometry.coordinates[0].map((c) => ({
          lat: c[1],
          lng: c[0],
        }));
      } else if (feature.geometry.type === "MultiPolygon") {
        paths = feature.geometry.coordinates[0][0].map((c) => ({
          lat: c[1],
          lng: c[0],
        }));
      } else {
        console.warn("Unsupported geometry type:", feature.geometry.type);
        return;
      }

      const polygon = new window.google.maps.Polygon({
        paths: paths,
        strokeColor: "#00B3A1",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#00B3A1",
        fillOpacity: 0.3,
        map: mapRef.current,
        clickable: true,
        geodesic: false,
      });

      const clickListener = window.google.maps.event.addListener(
        polygon,
        "click",
        (event) => {
          handlePolygonClick(polygon, index);
        }
      );

      polygon.featureIndex = index;
      polygon.clickListener = clickListener;
      polygons.push(polygon);
    });

    setEditablePolygons(polygons);
    editablePolygonsRef.current = polygons;
  };

  /**
   * @function handlePolygonClick
   * @description Toggles a polygon's deletion state and restyles it (teal when kept, red when
   * marked for deletion).
   * @param {google.maps.Polygon} polygon - The clicked polygon.
   * @param {Number} featureIndex - Index of the polygon's feature in the source GeoJSON.
   * **Called by:
   * @see {renderPolygons} - via each polygon's 'click' listener.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handlePolygonClick = (polygon, featureIndex) => {
    setDeletedFeatureIndices((prevIndices) => {
      const newDeletedIndices = new Set(prevIndices);

      if (newDeletedIndices.has(featureIndex)) {
        newDeletedIndices.delete(featureIndex);
        polygon.setOptions({
          strokeColor: "#00B3A1",
          fillColor: "#00B3A1",
          strokeOpacity: 0.8,
          fillOpacity: 0.3,
          strokeWeight: 2,
        });
      } else {
        newDeletedIndices.add(featureIndex);
        polygon.setOptions({
          strokeColor: "#FF0000",
          fillColor: "#FF0000",
          strokeOpacity: 1,
          fillOpacity: 0.5,
          strokeWeight: 3,
        });
      }

      return newDeletedIndices;
    });
  };

  /**
   * @function clearPolygons
   * @description Removes every editable polygon's click listener and map overlay, then clears
   * the polygon state.
   * **Called by:
   * @see {VectorEditor} - via handleApplyAndContinue, handleSkip, and the unmount cleanup effect.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const clearPolygons = () => {
    editablePolygonsRef.current.forEach((polygon) => {
      if (polygon.clickListener) {
        window.google.maps.event.removeListener(polygon.clickListener);
      }
      polygon.setMap(null);
    });
    setEditablePolygons([]);
    editablePolygonsRef.current = [];
  };

  /**
   * @function handleApplyAndContinue
   * @description Applies the user's polygon deletions to the cached vectors, images, and AWEI
   * layers, then advances to the finalize step.
   * 1. Confirms with the user if every polygon was marked for deletion.
   * 2. If any polygons were deleted, filters them out of the vector FeatureCollection and masks
   *    the deleted areas out of the cached step-5 images.
   * 3. If AWEI images are cached, re-masks and regenerates their thumbnail URLs excluding the
   *    deleted areas.
   * 4. Clears the map's editable polygons and navigates to the FINALIZE step.
   * @see {@link clearPolygons}
   * @see {@link OverlayHelper}
   * **Called by:
   * @see {VectorEditor} - via StepperButtons' onNext.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handleApplyAndContinue = () => {
    if (!vectorsGeoJSONRef.current) {
      dispatch(Snack.error("intertidal-no-vectors-found", t("forms.intertidal.noVectorsFound")));
      return;
    }

    if (
      deletedFeatureIndices.size === vectorsGeoJSONRef.current.features.length
    ) {
      const confirmed = window.confirm(t("forms.intertidal.deleteAllPolygonsConfirm"));
      if (!confirmed) return;
    }

    const geoJson = vectorsGeoJSONRef.current;

    if (deletedFeatureIndices.size > 0) {
      const filteredFeatures = geoJson.features.filter(
        (_, i) => !deletedFeatureIndices.has(i)
      );

      const filteredGeoJSON = { ...geoJson, features: filteredFeatures };
      const newVectors = ee.FeatureCollection(filteredGeoJSON);
      const deletedFeatures = geoJson.features.filter((_, i) =>
        deletedFeatureIndices.has(i)
      );

      const reclippedImages = OverlayHelper.imgsToStep5.map((img, idx) => {
        let processedImg = ee.Image(img);

        if (deletedFeatures.length > 0) {
          const deletedVectors = ee.FeatureCollection({
            ...geoJson,
            features: deletedFeatures,
          });
          const maskToRemove = ee.Image.constant(1)
            .clip(deletedVectors)
            .mask()
            .not();

          processedImg = processedImg.updateMask(maskToRemove);
        }

        return processedImg;
      });

      OverlayHelper.imgsToStep5 = reclippedImages;
      OverlayHelper.vectorsToExport = newVectors;

      if (OverlayHelper.aweiImages && OverlayHelper.aweiImages.length > 0) {
        const vectorBounds = newVectors.geometry().bounds().getInfo();
        const coords = vectorBounds.coordinates[0];

        const googleBounds = new window.google.maps.LatLngBounds();
        coords.forEach((coord) => {
          googleBounds.extend(
            new window.google.maps.LatLng(coord[1], coord[0])
          );
        });

        const vis_params = {
          min: -0.5,
          max: 1,
          dimensions: 512,
          palette: ["white", "blue"],
          region: newVectors.geometry(),
        };

        const newAweiURLs = OverlayHelper.aweiImages.map((aweiImg, idx) => {
          let processedAwei = aweiImg.image;

          if (deletedFeatures.length > 0) {
            const deletedVectors = ee.FeatureCollection({
              ...geoJson,
              features: deletedFeatures,
            });

            const maskToRemove = ee.Image.constant(1)
              .clip(deletedVectors)
              .mask()
              .not();

            processedAwei = processedAwei.updateMask(maskToRemove);
          }

          const newUrl = processedAwei.getThumbURL(vis_params);
          return {
            url: newUrl,
            date: aweiImg.date,
            bounds: googleBounds,
          };
        });

        OverlayHelper.aweiLayerURLs = newAweiURLs;

        if (deletedFeatures.length > 0) {
          const deletedVectors = ee.FeatureCollection({
            ...geoJson,
            features: deletedFeatures,
          });
          OverlayHelper.aweiImages = OverlayHelper.aweiImages.map((aweiImg) => {
            const maskToRemove = ee.Image.constant(1)
              .clip(deletedVectors)
              .mask()
              .not();
            return {
              image: aweiImg.image.updateMask(maskToRemove),
              date: aweiImg.date,
            };
          });
        }
      }
    } else {
      console.log(
        "No polygons deleted, using all",
        geoJson.features.length,
        "features"
      );
    }

    clearPolygons();

    navigate(FINALIZE);
  };

  /**
   * @function handleSkip
   * @description Discards any polygon edits and advances directly to the finalize step.
   * @see {@link clearPolygons}
   * **Called by:
   * @see {VectorEditor} - via the "Skip" button's onClick. (Previously also wired as
   * StepperButtons' own `navigate` prop, which broke the "back" button — StepperButtons calls
   * that same prop with `PREVIOUS` for its back button, and this function ignores its argument
   * and always finalizes, so clicking "back" landed on the result page instead of going back.)
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handleSkip = () => {
    clearPolygons();
    navigate(FINALIZE);
  };

  useEffect(() => {
    return () => {
      clearPolygons();
      if (aoiPolygonRef.current) {
        aoiPolygonRef.current.setMap(null);
      }
    };
  }, []);

  return (
    <React.Fragment>
      <Box>
        <GoogleMap
          style={{ width: "100%", height: 500 }}
          onRegionDrawn={() => { }}
        />

        <br />

        <Box textAlign="center" mt={3} mb={2}>
          <Typography
            variant="h6"
            style={{
              marginBottom: "10px",
              fontWeight: "bold",
              color: "#00A391",
            }}
          >
            {t("forms.intertidal.vectorEditor")}
          </Typography>
          <Typography variant="body1" style={{ marginBottom: "10px" }}>
            {t("forms.intertidal.clickPolygons")}
          </Typography>

          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginBottom: "15px" }}
          >
            {deletedFeatureIndices.size > 0
              ? t("forms.intertidal.deletedCount", { count: deletedFeatureIndices.size })
              : t("forms.intertidal.noneDeleted")}
          </Typography>

        </Box>

        <StepperButtons
          navigate={navigate}
          nextTarget={FINALIZE}
          onNext={handleApplyAndContinue}
        >
          <Button onClick={handleSkip} color="secondary" variant="outlined">
            {t("forms.intertidal.skip")}
          </Button>

        </StepperButtons>
      </Box>
    </React.Fragment>
  );
};

export default VectorEditor;
